import { FunctionComponent, useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './VitalsInput.module.css';

interface VitalsData {
  bloodPressure?: {
    systolic?: number;
    diastolic?: number;
  };
  temperature?: number;
  heartRate?: number;
  respiratoryRate?: number;
  oxygenSaturation?: number;
}

interface VitalsInputProps {
  label?: string;
  value: VitalsData;
  onChange: (vitals: VitalsData) => void;
  units?: {
    temperature?: 'fahrenheit' | 'celsius';
    weight?: 'lbs' | 'kg';
  };
  required?: boolean;
  showNormalRanges?: boolean;
  hasNextButton?: boolean;
  onNext?: () => void;
}

interface VitalRange {
  min: number;
  max: number;
  unit: string;
}

const normalRanges: Record<string, VitalRange> = {
  systolic: { min: 90, max: 120, unit: 'mmHg' },
  diastolic: { min: 60, max: 80, unit: 'mmHg' },
  temperatureFahrenheit: { min: 97.6, max: 99.6, unit: '°F' },
  temperatureCelsius: { min: 36.5, max: 37.5, unit: '°C' },
  heartRate: { min: 60, max: 100, unit: 'bpm' },
  respiratoryRate: { min: 12, max: 20, unit: 'breaths/min' },
  oxygenSaturation: { min: 95, max: 100, unit: '%' }
};

const VitalsInput: FunctionComponent<VitalsInputProps> = ({
  label = 'Enter your vital signs',
  value,
  onChange,
  units = { temperature: 'fahrenheit' },
  required = false,
  showNormalRanges = true,
  hasNextButton = false,
  onNext
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleBloodPressureChange = (type: 'systolic' | 'diastolic', val: string) => {
    const numVal = val === '' ? undefined : parseInt(val);
    onChange({
      ...value,
      bloodPressure: {
        ...value.bloodPressure,
        [type]: numVal
      }
    });
    
    // Validate
    if (numVal !== undefined) {
      const range = normalRanges[type];
      if (numVal < 50 || numVal > 250) {
        setErrors(prev => ({ ...prev, [type]: 'Please enter a valid blood pressure' }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[type];
          return newErrors;
        });
      }
    }
  };

  const handleTemperatureChange = (val: string) => {
    const numVal = val === '' ? undefined : parseFloat(val);
    onChange({ ...value, temperature: numVal });
    
    // Validate
    if (numVal !== undefined) {
      const isCelsius = units.temperature === 'celsius';
      const min = isCelsius ? 35 : 95;
      const max = isCelsius ? 42 : 107;
      
      if (numVal < min || numVal > max) {
        setErrors(prev => ({ ...prev, temperature: 'Please enter a valid temperature' }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.temperature;
          return newErrors;
        });
      }
    }
  };

  const handleNumberChange = (field: keyof VitalsData, val: string, min: number, max: number) => {
    const numVal = val === '' ? undefined : parseInt(val);
    onChange({ ...value, [field]: numVal });
    
    // Validate
    if (numVal !== undefined) {
      if (numVal < min || numVal > max) {
        setErrors(prev => ({ ...prev, [field]: `Please enter a value between ${min} and ${max}` }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const isInNormalRange = (value: number | undefined, rangeKey: string): boolean => {
    if (value === undefined) return true;
    const range = normalRanges[rangeKey];
    return value >= range.min && value <= range.max;
  };

  const tempUnit = units.temperature === 'celsius' ? '°C' : '°F';
  const tempRangeKey = units.temperature === 'celsius' ? 'temperatureCelsius' : 'temperatureFahrenheit';

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.vitalsGrid}>
        {/* Blood Pressure */}
        <div className={styles.vitalGroup}>
          <div className={styles.vitalHeader}>
            <FavoriteIcon className={styles.vitalIcon} />
            <h4 className={styles.vitalTitle}>Blood Pressure</h4>
          </div>
          <div className={styles.bloodPressureInputs}>
            <div className={styles.inputWrapper}>
              <input
                type="number"
                className={`${styles.vitalInput} ${
                  value.bloodPressure?.systolic && !isInNormalRange(value.bloodPressure.systolic, 'systolic') 
                    ? styles.warning : ''
                } ${errors.systolic ? styles.error : ''}`}
                placeholder="120"
                value={value.bloodPressure?.systolic || ''}
                onChange={(e) => handleBloodPressureChange('systolic', e.target.value)}
                min="50"
                max="250"
              />
              <span className={styles.inputLabel}>Systolic</span>
            </div>
            <span className={styles.separator}>/</span>
            <div className={styles.inputWrapper}>
              <input
                type="number"
                className={`${styles.vitalInput} ${
                  value.bloodPressure?.diastolic && !isInNormalRange(value.bloodPressure.diastolic, 'diastolic') 
                    ? styles.warning : ''
                } ${errors.diastolic ? styles.error : ''}`}
                placeholder="80"
                value={value.bloodPressure?.diastolic || ''}
                onChange={(e) => handleBloodPressureChange('diastolic', e.target.value)}
                min="40"
                max="150"
              />
              <span className={styles.inputLabel}>Diastolic</span>
            </div>
            <span className={styles.unit}>mmHg</span>
          </div>
          {showNormalRanges && (
            <span className={styles.normalRange}>
              Normal: {normalRanges.systolic.min}-{normalRanges.systolic.max}/{normalRanges.diastolic.min}-{normalRanges.diastolic.max}
            </span>
          )}
          {errors.systolic && <span className={styles.errorText}>{errors.systolic}</span>}
          {errors.diastolic && <span className={styles.errorText}>{errors.diastolic}</span>}
        </div>

        {/* Temperature */}
        <div className={styles.vitalGroup}>
          <div className={styles.vitalHeader}>
            <ThermostatIcon className={styles.vitalIcon} />
            <h4 className={styles.vitalTitle}>Temperature</h4>
          </div>
          <div className={styles.singleInput}>
            <input
              type="number"
              step="0.1"
              className={`${styles.vitalInput} ${
                value.temperature && !isInNormalRange(value.temperature, tempRangeKey) 
                  ? styles.warning : ''
              } ${errors.temperature ? styles.error : ''}`}
              placeholder={units.temperature === 'celsius' ? '37.0' : '98.6'}
              value={value.temperature || ''}
              onChange={(e) => handleTemperatureChange(e.target.value)}
              min={units.temperature === 'celsius' ? 35 : 95}
              max={units.temperature === 'celsius' ? 42 : 107}
            />
            <span className={styles.unit}>{tempUnit}</span>
          </div>
          {showNormalRanges && (
            <span className={styles.normalRange}>
              Normal: {normalRanges[tempRangeKey].min}-{normalRanges[tempRangeKey].max} {tempUnit}
            </span>
          )}
          {errors.temperature && <span className={styles.errorText}>{errors.temperature}</span>}
        </div>

        {/* Heart Rate */}
        <div className={styles.vitalGroup}>
          <div className={styles.vitalHeader}>
            <FavoriteIcon className={`${styles.vitalIcon} ${styles.heartbeat}`} />
            <h4 className={styles.vitalTitle}>Heart Rate</h4>
          </div>
          <div className={styles.singleInput}>
            <input
              type="number"
              className={`${styles.vitalInput} ${
                value.heartRate && !isInNormalRange(value.heartRate, 'heartRate') 
                  ? styles.warning : ''
              } ${errors.heartRate ? styles.error : ''}`}
              placeholder="70"
              value={value.heartRate || ''}
              onChange={(e) => handleNumberChange('heartRate', e.target.value, 30, 200)}
              min="30"
              max="200"
            />
            <span className={styles.unit}>bpm</span>
          </div>
          {showNormalRanges && (
            <span className={styles.normalRange}>
              Normal: {normalRanges.heartRate.min}-{normalRanges.heartRate.max} bpm
            </span>
          )}
          {errors.heartRate && <span className={styles.errorText}>{errors.heartRate}</span>}
        </div>

        {/* Respiratory Rate */}
        <div className={styles.vitalGroup}>
          <div className={styles.vitalHeader}>
            <AirIcon className={styles.vitalIcon} />
            <h4 className={styles.vitalTitle}>Respiratory Rate</h4>
          </div>
          <div className={styles.singleInput}>
            <input
              type="number"
              className={`${styles.vitalInput} ${
                value.respiratoryRate && !isInNormalRange(value.respiratoryRate, 'respiratoryRate') 
                  ? styles.warning : ''
              } ${errors.respiratoryRate ? styles.error : ''}`}
              placeholder="16"
              value={value.respiratoryRate || ''}
              onChange={(e) => handleNumberChange('respiratoryRate', e.target.value, 8, 40)}
              min="8"
              max="40"
            />
            <span className={styles.unit}>breaths/min</span>
          </div>
          {showNormalRanges && (
            <span className={styles.normalRange}>
              Normal: {normalRanges.respiratoryRate.min}-{normalRanges.respiratoryRate.max} breaths/min
            </span>
          )}
          {errors.respiratoryRate && <span className={styles.errorText}>{errors.respiratoryRate}</span>}
        </div>

        {/* Oxygen Saturation */}
        <div className={styles.vitalGroup}>
          <div className={styles.vitalHeader}>
            <WaterDropIcon className={styles.vitalIcon} />
            <h4 className={styles.vitalTitle}>Oxygen Saturation (SpO₂)</h4>
          </div>
          <div className={styles.singleInput}>
            <input
              type="number"
              className={`${styles.vitalInput} ${
                value.oxygenSaturation && !isInNormalRange(value.oxygenSaturation, 'oxygenSaturation') 
                  ? styles.warning : ''
              } ${errors.oxygenSaturation ? styles.error : ''}`}
              placeholder="98"
              value={value.oxygenSaturation || ''}
              onChange={(e) => handleNumberChange('oxygenSaturation', e.target.value, 70, 100)}
              min="70"
              max="100"
            />
            <span className={styles.unit}>%</span>
          </div>
          {showNormalRanges && (
            <span className={styles.normalRange}>
              Normal: {normalRanges.oxygenSaturation.min}-{normalRanges.oxygenSaturation.max}%
            </span>
          )}
          {errors.oxygenSaturation && <span className={styles.errorText}>{errors.oxygenSaturation}</span>}
        </div>
      </div>

      {showNormalRanges && (
        <div className={styles.disclaimer}>
          <p>Normal ranges are general guidelines. Your normal values may differ based on age, fitness level, and medical conditions.</p>
        </div>
      )}
      
      {hasNextButton && (
        <button 
          type="button" 
          className={styles.nextButton} 
          onClick={onNext}
        >
          Next
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

export default VitalsInput;